import React from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import './App.css';
import PrivacyPolicy from './components/PrivacyPolicy';
import kidsPlayIcon from './assets/apps/kids-play.png';
import brainGamesIcon from './assets/apps/brian-game.png';
import mathSprintArenaIcon from './assets/apps/math-sprint-arena.png';
import quizMasterIcon from './assets/apps/quizmaster-Icon.png';

function Home() {
  const navigate = useNavigate();

  // App data - expandable as you release more apps
  // Icons use consistent neutral style, colors are system-generated
  const apps = [
    {
      id: 1,
      title: 'Kids Play',
      description: 'Kids Learning App is a fun and interactive way for children to learn alphabets, numbers, animals, and rhymes.',
      icon: kidsPlayIcon,
      privacyPolicy: '/privacy-policy/kids-play',
      playStoreUrl: 'https://play.google.com/store/apps/details?id=com.appgola.kidsplay'
    },
    {
      id: 2,
      title: 'Quiz Master',
      description: 'Quiz Master - Topic-based practice quizzes with review, score tracking & difficulty levels.',
      icon: quizMasterIcon,
      privacyPolicy: '/privacy-policy/quiz-master',
      playStoreUrl: 'https://play.google.com/store/apps/details?id=com.appgola.quizmaster'
    },
     {
      id: 3,
      title: 'Math Sprint Arena',
      description: 'Math Sprint Arena is a fast-paced mobile math game that challenges players on their arithmetic skills through timed quizzes.',
      icon: mathSprintArenaIcon,
      privacyPolicy: '/privacy-policy/math-sprint-arena',
      playStoreUrl: 'https://play.google.com/store/apps/details?id=com.appgola.mathsprintarena'
    },
      {
      id: 4,
      title: 'Brain Games',
      description: 'A combination of Quick educational games and puzzles to boost your brain power and cognitive skills.',
      icon: brainGamesIcon,
      privacyPolicy: '/privacy-policy/brain-games',
      playStoreUrl: 'https://play.google.com/store/apps/details?id=com.appgola.braingame'
    }
  ];

  const handleCardClick = (app) => {
    navigate(app.privacyPolicy);
  };

  const handlePlayStoreClick = (e, url) => {
    e.stopPropagation();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Minimalist Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center shadow-md">
                <span className="text-white font-bold text-lg">A</span>
              </div>
              <h1 className="text-xl font-bold text-gray-900 tracking-tight">Appgola.com</h1>
            </div>
            <a 
              href="mailto:digipeopleapps@gmail.com"
              className="text-sm font-medium text-gray-600 hover:text-indigo-600 transition-colors hidden sm:block"
            >
              Contact
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {/* Hero Section - Minimalist */}
        <section className="mb-16 sm:mb-20">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
            Our Applications
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
            Discover our collection of Android apps designed to enhance your productivity and lifestyle.
          </p>
        </section>

        {/* Apps Grid - Consistent Design */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-16 sm:mb-20">
          {apps.map((app) => (
            <div
              key={app.id}
              onClick={() => handleCardClick(app)}
              className="group bg-white rounded-3xl p-8 border-2 border-gray-100 hover:border-indigo-200 hover:shadow-xl transition-all duration-300 cursor-pointer"
            >
              {/* App Icon */}
                <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-105 group-hover:rotate-3 transition-all duration-300">
                 <img
                  src={app.icon}
                  alt={`${app.title} icon`}
                  className="w-16 h-16 object-contain"
                  loading="lazy"
                />
              </div>

              {/* App Info */}
              <h3 className="text-2xl font-bold text-gray-900 mb-3 tracking-tight">
                {app.title}
              </h3>
              <p className="text-base text-gray-600 mb-6 line-clamp-3 leading-relaxed">
                {app.description}
              </p>

              {/* CTA Buttons */}
              <div className="space-y-3">
                <button 
                  onClick={(e) => handlePlayStoreClick(e, app.playStoreUrl)}
                  className="w-full py-3 px-4 bg-white border-2 border-indigo-600 text-indigo-600 text-sm font-semibold rounded-xl hover:bg-indigo-50 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                  <span>Download on Play Store</span>
                </button>
                <button className="w-full py-3 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-md hover:shadow-lg">
                  View Privacy Policy
                </button>
              </div>
              
            </div>
          ))}
        </section>

        {/* Contact Section - Minimalist */}
        <section className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-3xl p-10 sm:p-12 border-2 border-indigo-100 text-center">
          <h3 className="text-3xl font-bold text-gray-900 mb-4">
            Get in Touch
          </h3>
          <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto leading-relaxed">
            Have questions or feedback? We'd love to hear from you.
          </p>
          <a 
            href="mailto:digipeopleapps@gmail.com"
            className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            digipeopleapps@gmail.com
          </a>
        </section>
      </main>

      {/* Minimalist Footer */}
      <footer className="bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <p className="text-sm font-medium text-gray-600">
              © {new Date().getFullYear()} Appgola.com. All rights reserved.
            </p>
            <a 
              href="mailto:digipeopleapps@gmail.com"
              className="text-sm font-medium text-gray-600 hover:text-indigo-600 transition-colors"
            >
              digipeopleapps@gmail.com
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <Routes>
      {/* <Route path="/" element={<Home />} />
      <Route path="/privacy-policy/task-manager-pro" element={<PrivacyPolicy appId="task-manager-pro" />} />
      <Route path="/privacy-policy/fitness-tracker" element={<PrivacyPolicy appId="fitness-tracker" />} /> */}
        <Route path="/" element={<Home />} />
      {/* Dynamic route: /privacy-policy/:appId */}
      <Route path="/privacy-policy/:appId" element={<PrivacyPolicy />} />
    </Routes>
  );
}

export default App;
